/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */

import { GizmoMode, Events, GizmoTransformEvent } from '@sick-davinci/glow';
import Vue from 'vue/dist/vue.esm';
import { Euler, Object3D } from 'three';
import crownConnect from './crownConnect';
import { info } from './log';
import { ShapeEditor } from './ShapeEditors';

const APP_SELECTOR = '#gui';

const readObjectName = (obj) => obj && (obj.name || obj.uuid) || '';

type GizmoLockModes = 'local' | 'global' | 'view';
type GizmoLockTypes = 'translate' | 'rotate' | 'scale';

function getGizmoLockProp(obj: Object3D, mode: GizmoLockModes, type: GizmoLockTypes) {
  if (obj) {
    const { userData: { gizmoLock } } = obj;
    if (gizmoLock) {
      const gizmoLockMode = gizmoLock[mode];
      if (gizmoLockMode) {
        const values = gizmoLockMode[type];
        // TODO mh.. not sure what to do with multipe values here ... this could really explode the gui :-/
        // tslint:disable-next-line:no-bitwise
        return values.reduce((result: number, value: number) => result | value, 0);
      }
    }
  }
  return 0;
}

function setGizmoLockProp(obj: Object3D, mode: GizmoLockModes, type: GizmoLockTypes, value: any) {
  if (obj) {
    const { userData: { gizmoLock } } = obj;
    if (!gizmoLock) {
      obj.userData.gizmoLock = {
        local: {
          translate: [0],
          rotate: [0],
          scale: [0],
        },
        global: {
          translate: [0],
          rotate: [0],
          scale: [0],
        },
        view: {
          translate: [0],
          rotate: [0],
          scale: [0],
        },
      };
    }
    obj.userData.gizmoLock[mode][type] = [parseInt(value, 10)];
    return obj.userData.gizmoLock;
  }
}

function readGizmoLockValues(app: any, obj: Object3D) {
  ['translate', 'rotate', 'scale'].forEach((type: GizmoLockTypes) => {
    app.gizmoLock[type] = getGizmoLockProp(obj, app.gizmoMode, type);
  });
}

export default ({stage, director, getEditor, onEditorChanged, changeEditor, activeObject, setHandleShapeByEngine}: ShapeEditor) => {

  const app = new Vue({
    el: APP_SELECTOR,
    data: {
      crownHost: '127.0.0.1',
      crownPort: 80,
      viewId: '3D',
      crownAction: 'connect!',
      loading: false,
      connected: false,
      hasActiveObject: activeObject.exists(), // !!getEditor().getObject(),
      objectName: readObjectName(getEditor().getObject()),
      translateX: 0,
      translateY: 0,
      translateZ: 0,
      rotateX: 0,
      rotateY: 0,
      rotateZ: 0,
      scaleX: 0,
      scaleY: 0,
      scaleZ: 0,
      editorType: 'Axis',
      gizmoMode: 'local',
      canChangeGizmoMode: true,
      canChangeGizmoLock: true,
      gizmoLock: {
        translate: 0,
        rotate: 0,
        scale: 0,
      },
    },
    methods: {
      connect() {
        app.loading = true;
        crownConnect(stage, director, getEditor, `${app.crownHost}:${app.crownPort}`, app.viewId, setHandleShapeByEngine).then(() => {
          app.connected = true;
          app.loading = false;
          getEditor().setObject(null);
        });
      },

      undo: () => getEditor().undo(),
      redo: () => getEditor().redo(),

      changeEditor(type) {
        app.editorType = type;
        app.canChangeGizmoMode = type === 'Axis';
        changeEditor(type);
      },

      changeGizmoMode(mode) {
        app.gizmoMode = mode;
        switch (mode) {
          case 'local':
            getEditor().setGizmoMode(GizmoMode.Local);
            break;
          case 'global':
            getEditor().setGizmoMode(GizmoMode.Global);
            break;
          case 'view':
          default:
            getEditor().setGizmoMode(GizmoMode.View);
        }
      },

      changeGizmoLock(type, value) {
        const obj = activeObject.get();
        const gizmoLock = setGizmoLockProp(activeObject.get(), app.gizmoMode, type, value);
        console.log('update gizmoLock: object=', readObjectName(obj), gizmoLock);
        getEditor().setObject(null);
        getEditor().setObject(obj);
      },
    },
  });

  activeObject.subscribe((obj: Object3D) => {
    app.hasActiveObject = !!obj;
    app.objectName = readObjectName(obj);
    setOrientation(obj, app);
    if (app.hasActiveObject) {
      readGizmoLockValues(app, obj);
    }
  });

  readGizmoLockValues(app, activeObject.get());

  const registerEditorListener = () => {
    getEditor().addEventListener(Events.GIZMOTRANSFORM, ({detail: {object: obj}}: CustomEvent<GizmoTransformEvent>) => {
      setOrientation(obj, app);
    });
  };

  registerEditorListener();

  onEditorChanged(() => {
    info('[onEditorChanged]', getEditor());
    registerEditorListener();
  });

};

const round = (x, c = 1) => {
  const n = parseInt('1'.padEnd(c + 1, '0'), 10);
  return Math.round(x * n) / n;
};

function setOrientation(obj: Object3D, app: any) {
  if (obj) {
    const { position, quaternion, scale } = obj;
    app.translateX = round(position.x);
    app.translateY = round(position.y);
    app.translateZ = round(position.z);
    const rotate = new Euler();
    const RAD2DEG = 180 / Math.PI;
    rotate.setFromQuaternion(quaternion);
    app.rotateX = round(rotate.x * RAD2DEG);
    app.rotateY = round(rotate.y * RAD2DEG);
    app.rotateZ = round(rotate.z * RAD2DEG);
    app.scaleX = round(scale.x, 2);
    app.scaleY = round(scale.y, 2);
    app.scaleZ = round(scale.z, 2);
  }
}

